import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useModuleAccess } from '../../../hooks/useModuleAccess';
import { PERMISSIONS } from '../../../utils/permissions';
import {
  getGuardrailsEnvironments,
  triggerEnvironmentScan,
  getComplianceStats,
} from '../../../services/cloudopsApi';
import { FiShield as Shield, FiPlay as Play, FiExternalLink as ExternalLink } from 'react-icons/fi';
import { Card, CardContent } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';

interface ManagedEnvironment {
  id: string;
  name?: string;
  aws_account_id: string;
  description?: string;
  environment_type?: string;
}

interface EnvironmentComplianceStats {
  compliance_score: number;
  [key: string]: unknown;
}

const EnvironmentsManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientId, isLoadingClients } = useClient();
  const { hasPermission } = usePermissions();
  const { isAWSUser } = useModuleAccess();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [environments, setEnvironments] = useState<ManagedEnvironment[]>([]);
  const [complianceStats, setComplianceStats] = useState<Record<string, EnvironmentComplianceStats | null>>({});
  const [scanningEnvironment, setScanningEnvironment] = useState<string | null>(null);

  useEffect(() => {
    // Avoid calling /guardrails/environments without client_id for partner/aws users.
    if (!user) return;
    const needsClientId = isAWSUser() ||
      hasPermission(PERMISSIONS.CLIENTS_VIEW) ||
      hasPermission(PERMISSIONS.CLIENTS_MANAGE) ||
      hasPermission(PERMISSIONS.PARTNERS_VIEW) ||
      hasPermission(PERMISSIONS.PARTNERS_MANAGE);
    if (needsClientId && !selectedClientId) {
      // While ClientContext is fetching/auto-selecting, keep the spinner instead of triggering an API error.
      if (isLoadingClients) return;
      setLoading(false);
      setError('Please select a client to view AWS environments.');
      return;
    }
    loadEnvironments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, isLoadingClients, user, hasPermission, isAWSUser]);

  const loadEnvironments = async () => {
    setLoading(true);
    setError(null);

    try {
      const clientId = selectedClientId || user?.client_id || undefined;
      const environmentsData = await getGuardrailsEnvironments(clientId);
      setEnvironments(environmentsData || []);

      // Load compliance stats for each environment
      const statsPromises = (environmentsData || []).map(async (environment: ManagedEnvironment) => {
        try {
          const stats = await getComplianceStats(environment.id);
          return { environmentId: environment.id, stats };
        } catch (err: unknown) {
          console.error('Failed to load compliance stats for environment', environment.id, err);
          return { environmentId: environment.id, stats: null };
        }
      });

      const statsResults = await Promise.all(statsPromises);
      const statsMap: Record<string, EnvironmentComplianceStats | null> = {};
      statsResults.forEach(({ environmentId, stats }) => {
        statsMap[environmentId] = stats;
      });
      setComplianceStats(statsMap);
    } catch (err: unknown) {
      console.error('Error loading environments:', err);
      setError(err instanceof Error ? err.message : 'Failed to load AWS environments');
    } finally {
      setLoading(false);
    }
  };

  const handleScan = async (environmentId: string) => {
    setScanningEnvironment(environmentId);
    try {
      await triggerEnvironmentScan(environmentId, 'full');
      toast.success('Scan triggered successfully');
      loadEnvironments();
    } catch (err: unknown) {
      console.error('Error triggering scan:', err);
      toast.error('Failed to trigger scan: ' + (err instanceof Error ? err.message : 'Unknown error'));
    } finally {
      setScanningEnvironment(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              AWS Environments
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              View connected AWS environments for guardrails scanning
            </p>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
          <Button
            onClick={loadEnvironments}
            variant="ghost"
            size="sm"
            className="mt-2"
          >
            Try again
          </Button>
        </div>
      )}

      {/* Environments List */}
      {environments.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {environments.map((environment) => {
            const stats = complianceStats[environment.id];
            return (
              <Card key={environment.id} hoverable>
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {environment.name || environment.aws_account_id}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {environment.aws_account_id}
                        </p>
                        {environment.description && (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {environment.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    {stats && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Compliance:</span>
                        <span className={`font-semibold ${
                          stats.compliance_score >= 95
                            ? 'text-green-600 dark:text-green-400'
                            : stats.compliance_score >= 80
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {stats.compliance_score || 0}%
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Type:</span>
                      <span className="text-gray-900 dark:text-white capitalize">
                        {environment.environment_type || 'aws'}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/cloudops/guardrails/environments/${environment.id}`)}
                      className="flex-1"
                    >
                      <ExternalLink className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleScan(environment.id)}
                      disabled={scanningEnvironment === environment.id}
                      className="flex-1"
                    >
                      <Play className="w-4 h-4 mr-1" />
                      Scan
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No AWS environments available
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              AWS environments are automatically available from the core service. Connect environments in the AWS Environments section.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EnvironmentsManagementPage;

